// ============================================================================
// Témoins exacts (lot 2 de la refonte, docs/moteur/REFONTE_MOTEUR.md).
//
//   - `sampleEnvs` : valeurs RATIONNELLES des inconnues, déterministes, qui
//     respectent les hypothèses (signes, entiers, expressions non nulles) ;
//   - `univariateSolutions` : solutions réelles EXACTES d'une équation en une
//     inconnue — racines rationnelles, puis formule du second degré sur ce qui
//     reste (racines écrites avec √, comparées exactement).
// Un contre-exemple trouvé ici est une PREUVE (proof: 'exact').
// ============================================================================

import { factsFrom, type Facts } from './conditions';
import * as Q from './exact';
import type { Rat } from './exact';
import {
  polyAdd,
  polyMul,
  polySub,
  polyDivExact,
  polyPow,
  polyRat,
  polyScale,
  constPoly,
  evalExact,
  evalFloat,
  NormalFail,
  polyFloat,
  polyIsZero,
  polyNeg,
  polyToTerm,
  rationalRoots,
  rfDiv,
  rfIsZero,
  rfSimplify,
  rfToTerm,
  toRatFn,
  type Poly,
  type RatFn,
} from './normal';
import { add, div, fn, mul, num, substitute, sym, termKey, type Term } from './term';

/** Valeurs essayées, dans cet ordre de préférence (petites, variées, non symétriques). */
export const SAMPLE_POOL: readonly Rat[] = [
  [2, 1],
  [3, 1],
  [-1, 1],
  [1, 2],
  [-2, 1],
  [5, 3],
  [1, 1],
  [-3, 2],
  [4, 1],
  [-5, 1],
  [7, 2],
  [1, 3],
  [-2, 3],
  [6, 1],
  [-7, 1],
  [10, 1],
  [0, 1],
  [9, 4],
  [-1, 2],
  [11, 1],
].map(([n, d]) => Q.rat(n!, d!));

function fnv(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) h = Math.imul(h ^ s.charCodeAt(i), 0x01000193) >>> 0;
  return h;
}
function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function allowed(name: string, v: Rat, facts: Facts): boolean {
  const s = facts.sign.get(name);
  if (s) {
    const sg = Q.sign(v);
    if ((sg > 0 && !s.pos) || (sg < 0 && !s.neg) || (sg === 0 && !s.zero)) return false;
  }
  if (facts.integer.has(name) && !Q.isInteger(v)) return false;
  return true;
}
function respectsPolys(env: Record<string, Rat>, facts: Facts): boolean {
  for (const h of facts.polys) {
    let v: Rat | null;
    try {
      v = evalExact(polyToTerm(h.p), env);
    } catch {
      return false;
    }
    if (v === null) continue;
    const sg = Q.sign(v);
    if ((sg > 0 && !h.s.pos) || (sg < 0 && !h.s.neg) || (sg === 0 && !h.s.zero)) return false;
  }
  return true;
}

/** Jusqu'à `count` jeux de valeurs exactes respectant les faits (déterministes pour `seed`). */
export function sampleEnvs(
  symbols: readonly string[],
  facts: Facts = factsFrom(),
  seed = '',
  count = 12,
): Record<string, Rat>[] {
  const names = [...symbols].sort();
  if (!names.length) return [{}];
  const pools = names.map((n) => SAMPLE_POOL.filter((v) => allowed(n, v, facts)));
  if (pools.some((p) => !p.length)) return [];
  const out: Record<string, Rat>[] = [];
  const seen = new Set<string>();
  const rnd = mulberry32(fnv(`${seed}|${names.join(',')}`));
  for (let tries = 0; out.length < count && tries < count * 20; tries++) {
    const env: Record<string, Rat> = {};
    names.forEach((n, i) => {
      const pool = pools[i]!;
      // 1er essai : les valeurs préférées, ensuite au hasard (déterministe)
      env[n] = tries === 0 ? pool[i % pool.length]! : pool[Math.floor(rnd() * pool.length)]!;
    });
    const key = names.map((n) => Q.ratToText(env[n]!)).join(',');
    if (seen.has(key) || !respectsPolys(env, facts)) continue;
    seen.add(key);
    out.push(env);
  }
  return out;
}

/* ------------------------------ une inconnue ------------------------------ */

/** Coefficients (indice = degré) d'un polynôme en la seule inconnue `name`, sinon null. */
export function univariateCoefs(p: Poly, name: string): Rat[] | null {
  const out: Rat[] = [];
  for (const { m, c } of p.t.values()) {
    if (m.length > 1 || (m.length === 1 && m[0]!.a.symbol !== name)) return null;
    const e = m.length ? m[0]!.e : 0;
    while (out.length <= e) out.push(Q.ZERO);
    out[e] = c;
  }
  return out;
}

/** Division synthétique par (x − r). */
function deflate(coefs: Rat[], r: Rat): Rat[] {
  const n = coefs.length - 1;
  const out: Rat[] = new Array<Rat>(n).fill(Q.ZERO);
  let acc = Q.ZERO;
  for (let i = n; i >= 1; i--) {
    acc = Q.add(Q.mul(acc, r), coefs[i]!);
    out[i - 1] = acc;
  }
  return out;
}
function trim(c: Rat[]): Rat[] {
  const out = [...c];
  while (out.length > 1 && Q.isZero(out[out.length - 1]!)) out.pop();
  return out;
}

export interface Solutions {
  /** solutions réelles exactes (sans doublon) */
  roots: Term[];
  /** toutes les solutions réelles sont-elles connues ? */
  complete: boolean;
}

/**
 * Solutions réelles de « E = 0 » (E en la seule inconnue `name`, les autres
 * lettres déjà remplacées). Les zéros du dénominateur sont exclus.
 */
export function univariateSolutions(e: RatFn, name: string, depth = 0): Solutions | null {
  const coefs = univariateCoefs(e.n, name);
  if (!coefs)
    return (
      monomialZeros(e, name) ??
      lnDifference(e, name, depth) ??
      linearWithConstants(e, name) ??
      byInversion(e, name, depth)
    );
  let rest = trim(coefs);
  if (rest.length === 1) return Q.isZero(rest[0]!) ? null : { roots: [], complete: true };
  // trop de diviseurs à essayer (1 048 576, 8·10¹⁵) : degré 1 ou 2, on résout directement
  // (lot 3 : « R = 800000000 → R = 8000000000000000 » restait indécidé)
  const rr = rationalRoots(e.n, name) ?? (rest.length <= 3 ? [] : null);
  if (!rr) return null;
  const roots: Term[] = rr.map((r) => num(r));
  for (const r of rr) {
    // retirer toutes les occurrences de la racine
    for (let k = 0; k < 64 && rest.length > 1; k++) {
      let v = Q.ZERO;
      for (let i = rest.length - 1; i >= 0; i--) v = Q.add(Q.mul(v, r), rest[i]!);
      if (!Q.isZero(v)) break;
      rest = trim(deflate(rest, r));
    }
  }
  let complete = rest.length <= 1;
  if (rest.length === 2) {
    roots.push(num(Q.neg(Q.div(rest[0]!, rest[1]!)!)));
    complete = true;
  }
  if (rest.length === 3) {
    const [c, b, a] = rest as [Rat, Rat, Rat];
    const delta = Q.sub(Q.mul(b, b), Q.mul(Q.rat(4), Q.mul(a, c)));
    complete = true;
    if (Q.sign(delta) >= 0) {
      for (const s of Q.sign(delta) === 0 ? [1] : [-1, 1]) {
        const top = add(num(Q.neg(b)), mul(num(s), fn('sqrt', num(delta))));
        roots.push(div(top, num(Q.mul(Q.rat(2), a))));
      }
    }
  }
  // bicarré a x⁴ + b x² + c (lot 6.18) : t = x² ; seulement quand les racines en t sont
  // rationnelles ou toutes deux négatives (« (1 + x²)² » au dénominateur : aucune racine)
  if (rest.length === 5 && Q.isZero(rest[1]!) && Q.isZero(rest[3]!)) {
    const ts = biquadraticT(rest[0]!, rest[2]!, rest[4]!);
    if (ts) {
      complete = true;
      for (const t of ts) {
        if (Q.sign(t) < 0) continue;
        if (Q.isZero(t)) {
          roots.push(num(Q.ZERO));
          continue;
        }
        const sq = Q.sqrtRat(t);
        const r = sq && Q.radIsRational(sq) ? num(sq.coef) : fn('sqrt', num(t));
        roots.push(mul(num(Q.rat(-1)), r), r);
      }
    }
  }
  // zéros du dénominateur exclus
  const kept = roots.filter((r) => {
    try {
      const d = toRatFn(substitute(polyToTerm(e.d), { [name]: r }));
      return !rfIsZero(d);
    } catch (err) {
      if (err instanceof NormalFail) return false;
      throw err;
    }
  });
  return { roots: kept, complete };
}

/**
 * Racines en t de a t² + b t + c (t = x², lot 6.18) : rationnelles (sans doublon), [] si
 * aucune racine réelle ou toutes deux négatives ; null si une racine est irrationnelle et
 * positive (√ d'un radical : on ne sait pas l'écrire exactement).
 */
function biquadraticT(c: Rat, b: Rat, a: Rat): Rat[] | null {
  const delta = Q.sub(Q.mul(b, b), Q.mul(Q.rat(4), Q.mul(a, c)));
  if (Q.sign(delta) < 0) return [];
  const sq = Q.sqrtRat(delta);
  if (sq && Q.radIsRational(sq)) {
    const out: Rat[] = [];
    for (const s of Q.sign(delta) === 0 ? [1] : [-1, 1]) {
      const t = Q.div(Q.add(Q.neg(b), Q.mul(Q.rat(s), sq.coef)), Q.mul(Q.rat(2), a));
      if (t && !out.some((u) => Q.eq(u, t))) out.push(t);
    }
    return out;
  }
  // deux racines irrationnelles : somme −b ÷ a < 0 et produit c ÷ a > 0 → toutes deux négatives
  const sum = Q.div(Q.neg(b), a);
  const prod = Q.div(c, a);
  return sum && prod && Q.sign(sum) < 0 && Q.sign(prod) > 0 ? [] : null;
}

/**
 * Coefficients CONSTANTS non rationnels (π, √2, cos(35°), ln 2…) : seulement le
 * premier degré, a·x + b = 0 → x = −b ÷ a (lot 3 bis : « R = 18π », « BC = 7 ÷ cos(35°) »).
 * Au-delà, la racine carrée d'un discriminant constant resterait opaque : null.
 */
/**
 * Un seul monôme : c × xᵏ × exp(…)… = 0. Une exponentielle ne s'annule jamais
 * (lot 3 bis : dénominateur (2ⁿ)² × 5ⁿ de 1,05ⁿ − 2) ; x s'annule en 0.
 */
function monomialZeros(e: RatFn, name: string): Solutions | null {
  const terms = [...e.n.t.values()];
  if (terms.length !== 1) return null;
  let zero = false;
  for (const f of terms[0]!.m) {
    if (f.a.kind === 'sym' && f.a.symbol === name) zero = true;
    else if (f.a.kind === 'opaque' && f.a.term.k === 'fn' && f.a.term.f === 'exp') continue;
    else if (!f.a.constant) return null;
  }
  return keepDefined(e, name, zero ? [num(Q.ZERO)] : []);
}

/**
 * c ln(A) − c ln(B) = 0 (A, B dépendant de x) : A = B, avec A > 0 et B > 0
 * (lot « exp / ln » : ln(2x + 1) = ln(x + 3)).
 */
function lnDifference(e: RatFn, name: string, depth: number): Solutions | null {
  const terms = [...e.n.t.values()];
  if (terms.length !== 2) return null;
  const args: Term[] = [];
  for (const t of terms) {
    if (t.m.length !== 1 || t.m[0]!.e !== 1) return null;
    const a = t.m[0]!.a;
    if (a.kind !== 'opaque' || a.constant || a.term.k !== 'fn' || a.term.f !== 'ln') return null;
    args.push(a.term.a);
  }
  if (!Q.isZero(Q.add(terms[0]!.c, terms[1]!.c))) return null;
  let diff: RatFn;
  try {
    diff = toRatFn(add(args[0]!, mul(num(-1), args[1]!)));
  } catch (err) {
    if (err instanceof NormalFail) return null;
    throw err;
  }
  const sol = univariateSolutions(diff, name, depth + 1);
  if (!sol) return null;
  const positive = (t: Term, r: Term): boolean => {
    const v = evalFloat(substitute(t, { [name]: r }), {});
    return Number.isFinite(v) && v > 1e-9;
  };
  const kept = sol.roots.filter((r) => positive(args[0]!, r) && positive(args[1]!, r));
  const k = keepDefined(e, name, kept);
  return k && { roots: k.roots, complete: sol.complete };
}

function linearWithConstants(e: RatFn, name: string): Solutions | null {
  const byDeg = new Map<number, Poly>();
  for (const { m, c } of e.n.t.values()) {
    let deg = 0;
    const rest: typeof m = [];
    for (const f of m) {
      if (f.a.kind === 'sym' && f.a.symbol === name) deg += f.e;
      else if (f.a.constant) rest.push(f);
      else return null;
    }
    const key = rest.map((f) => (f.e === 1 ? f.a.key : `${f.a.key}^${f.e}`)).join('·');
    const p = byDeg.get(deg) ?? { t: new Map() };
    const prev = p.t.get(key);
    p.t.set(key, { m: rest, c: prev ? Q.add(prev.c, c) : c });
    if (prev && Q.isZero(p.t.get(key)!.c)) p.t.delete(key);
    byDeg.set(deg, p);
  }
  const deg = Math.max(
    0,
    ...[...byDeg.entries()].filter(([, p]) => !polyIsZero(p)).map(([d]) => d),
  );
  if (deg === 0) {
    const c0 = byDeg.get(0);
    return !c0 || polyIsZero(c0) ? null : { roots: [], complete: true };
  }
  if (deg !== 1) {
    // coefficients tous multiples du dominant (π r² − 50π) : on divise, et c'est rationnel
    const lead = byDeg.get(deg)!;
    const coefs: Rat[] = [];
    for (let k = 0; k <= deg; k++) {
      const c = byDeg.get(k);
      if (!c || polyIsZero(c)) {
        coefs.push(Q.ZERO);
        continue;
      }
      const q = polyDivExact(c, lead);
      const r = q ? polyRat(q) : null;
      if (r === null) return deg === 2 ? quadraticWithConstants(e, name, byDeg) : null;
      coefs.push(r);
    }
    let p: Poly = { t: new Map() };
    const x = toRatFn(sym(name)).n;
    coefs.forEach((c, k) => {
      if (!Q.isZero(c)) p = polyAdd(p, polyScale(polyPow(x, k), c));
    });
    return univariateSolutions({ n: p, d: e.d }, name);
  }
  const a = byDeg.get(1)!;
  const b = byDeg.get(0) ?? { t: new Map() };
  return keepDefined(e, name, rootOfLinear(a, b));
}

function rootOfLinear(a: Poly, b: Poly): Term[] | null {
  // un coefficient « constant » peut être nul sans que la forme le voie (ln 4 − 2 ln 2)
  const av = polyFloat(a);
  if (!Number.isFinite(av) || Math.abs(av) < 1e-9) return null;
  const one = constPoly(Q.ONE);
  return [rfToTerm(rfSimplify(rfDiv({ n: polyNeg(b), d: one }, { n: a, d: one })))];
}

/** Racines retenues : celles qui n'annulent pas le dénominateur. */
function keepDefined(e: RatFn, name: string, roots: Term[] | null): Solutions | null {
  if (!roots) return null;
  const kept = roots.filter((r) => {
    try {
      return !rfIsZero(toRatFn(substitute(polyToTerm(e.d), { [name]: r })));
    } catch (err) {
      if (err instanceof NormalFail) return false;
      throw err;
    }
  });
  return { roots: kept, complete: true };
}

/**
 * a x² + b x + c = 0, coefficients constants non rationnels (π d² − 100π, lot 3 bis) :
 * discriminant exact, signe décidé en flottants avec marge (sinon indécis).
 */
function quadraticWithConstants(
  e: RatFn,
  name: string,
  byDeg: Map<number, Poly>,
): Solutions | null {
  const zero: Poly = { t: new Map() };
  const a = byDeg.get(2)!;
  const b = byDeg.get(1) ?? zero;
  const c = byDeg.get(0) ?? zero;
  const av = polyFloat(a);
  if (!Number.isFinite(av) || Math.abs(av) < 1e-9) return null;
  const delta = polySub(polyMul(b, b), polyScale(polyMul(a, c), Q.rat(4)));
  const two = polyScale(a, Q.rat(2));
  const mb = polyToTerm(polyNeg(b));
  if (polyIsZero(delta)) return keepDefined(e, name, [div(mb, polyToTerm(two))]);
  const dv = polyFloat(delta);
  if (!Number.isFinite(dv) || Math.abs(dv) < 1e-9) return null;
  if (dv < 0) return { roots: [], complete: true };
  const root = fn('sqrt', polyToTerm(delta));
  return keepDefined(
    e,
    name,
    [-1, 1].map((s) => div(add(mb, mul(num(Q.rat(s)), root)), polyToTerm(two))),
  );
}

const FRESH = '§';
function replaceKey(t: Term, key: string, by: Term): Term {
  if (termKey(t) === key) return by;
  switch (t.k) {
    case 'add':
    case 'mul':
      return { k: t.k, c: t.c.map((c) => replaceKey(c, key, by)) };
    case 'div':
    case 'pow':
    case 'binom':
      return { k: t.k, a: replaceKey(t.a, key, by), b: replaceKey(t.b, key, by) };
    case 'fn':
      return { k: 'fn', f: t.f, a: replaceKey(t.a, key, by) };
    default:
      return t;
  }
}

/**
 * L'inconnue n'apparaît que dans UNE fonction exp, ln ou √ (lot 3 bis : « ln(x) + 1 = 0 »,
 * « 3 × exp(2x) − 6 = 0 ») : on résout en cette fonction, puis on l'inverse
 * (exp(u) = c → u = ln c si c > 0 ; ln(u) = c → u = exp(c) ; √u = c → u = c² si c ≥ 0).
 */
function byInversion(e: RatFn, name: string, depth: number): Solutions | null {
  if (depth > 2) return null;
  const lin = byLogarithm(e, name, depth);
  if (lin) return lin;
  let atom: { key: string; term: Term } | null = null;
  for (const { m } of e.n.t.values())
    for (const f of m) {
      if (f.a.constant) continue;
      if (f.a.kind !== 'opaque') return null;
      if (atom && atom.key !== f.a.key) return null;
      atom = { key: f.a.key, term: f.a.term };
    }
  const at = atom?.term;
  if (!at) return null;
  const isFn = at.k === 'fn' && (at.f === 'exp' || at.f === 'ln' || at.f === 'sqrt');
  // b^u (b constante > 0) ou u^(1/r)
  const pw = at.k === 'pow' ? powInverse(at, name) : null;
  if (!isFn && !pw) return null;
  let inAtom: RatFn;
  try {
    inAtom = toRatFn(replaceKey(polyToTerm(e.n), atom!.key, sym(FRESH)));
  } catch (err) {
    if (err instanceof NormalFail) return null;
    throw err;
  }
  const outer = univariateSolutions({ n: inAtom.n, d: constPoly(Q.ONE) }, FRESH, depth + 1);
  if (!outer) return null;
  const roots: Term[] = [];
  let complete = outer.complete;
  for (const c of outer.roots) {
    const cv = evalFloat(c, {});
    if (!Number.isFinite(cv)) return null;
    let target: Term;
    let arg: Term;
    if (pw) {
      const t = pw.target(c, cv);
      if (!t) continue;
      target = t;
      arg = pw.arg;
    } else if (at.k === 'fn' && at.f === 'exp') {
      if (cv <= 1e-12) continue;
      target = fn('ln', c);
      arg = at.a;
    } else if (at.k === 'fn' && at.f === 'ln') {
      target = fn('exp', c);
      arg = at.a;
    } else if (at.k === 'fn') {
      if (cv < -1e-12) continue;
      target = mul(c, c);
      arg = at.a;
    } else return null;
    let inner: RatFn;
    try {
      inner = toRatFn(add(arg, mul(num(-1), target)));
    } catch (err) {
      if (err instanceof NormalFail) return null;
      throw err;
    }
    const sub = univariateSolutions(inner, name, depth + 1);
    if (!sub) return null;
    complete &&= sub.complete;
    roots.push(...sub.roots);
  }
  const kept = roots.filter((r) => {
    try {
      return !rfIsZero(toRatFn(substitute(polyToTerm(e.d), { [name]: r })));
    } catch (err) {
      if (err instanceof NormalFail) return false;
      throw err;
    }
  });
  return { roots: kept, complete };
}

/** Inverse d'une puissance : b^u = c → u = ln c ÷ ln b ; u^(1/r) = c → u = c^r. */
function powInverse(
  at: Extract<Term, { k: 'pow' }>,
  name: string,
): { arg: Term; target: (c: Term, cv: number) => Term | null } | null {
  const hasX = (t: Term): boolean => {
    try {
      return [...toRatFn(t).n.t.values(), ...toRatFn(t).d.t.values()].some((x) =>
        x.m.some((f) => !f.a.constant),
      );
    } catch {
      return true;
    }
  };
  if (!hasX(at.a) && hasX(at.b)) {
    const b = evalFloat(at.a, {});
    if (!(b > 0) || Math.abs(b - 1) < 1e-12) return null;
    return {
      arg: at.b,
      target: (c, cv) => (cv > 1e-12 ? div(fn('ln', c), fn('ln', at.a)) : null),
    };
  }
  if (hasX(at.a) && !hasX(at.b)) {
    const k = toRatFn(at.b);
    const n = k.n.t.size === 1 ? [...k.n.t.values()][0]! : null;
    const d = k.d.t.size === 1 ? [...k.d.t.values()][0]! : null;
    if (!n || !d || n.m.length || d.m.length) return null;
    const q = Q.div(n.c, d.c)!;
    // u^(1/r), r entier ≥ 2
    if (q.n !== 1n || q.d < 2n || q.d > 12n) return null;
    const r = Number(q.d);
    return {
      arg: at.a,
      target: (c, cv) => (r % 2 === 0 && cv < -1e-12 ? null : { k: 'pow', a: c, b: num(r) }),
    };
  }
  void name;
  return null;
}

/**
 * Deux termes en exponentielles : a × exp(u)ᵏ… = b × exp(v)ˡ… → on passe au logarithme
 * (lot 3 bis : « exp(2x − 1) = 3 », que la forme normale écrit exp(x)² = 3e).
 */
function byLogarithm(e: RatFn, name: string, depth: number): Solutions | null {
  const terms = [...e.n.t.values()];
  if (terms.length !== 2) return null;
  const parts: Term[] = [];
  const signs: number[] = [];
  let usesX = false;
  for (const [i, t] of terms.entries()) {
    // c₁m₁ + c₂m₂ = 0  ⟺  c₁m₁ = −c₂m₂ : on prend le logarithme de chaque membre
    const c = i === 0 ? t.c : Q.neg(t.c);
    signs.push(Q.sign(c));
    const logs: Term[] = [fn('ln', num(Q.abs(c)))];
    for (const f of t.m) {
      if (f.a.kind === 'root') logs.push(mul(num(Q.rat(f.e, 2)), fn('ln', num(f.a.prime!))));
      else if (f.a.kind === 'opaque' && f.a.term.k === 'fn' && f.a.term.f === 'exp') {
        if (!f.a.constant) usesX = true;
        logs.push(mul(num(f.e), f.a.term.a));
      } else if (f.a.kind === 'opaque' && f.a.term.k === 'pow' && positiveConstant(f.a.term.a)) {
        // bᵘ, b constante > 0 : ln(bᵘ) = u ln b (2^x = 3^(x − 1))
        if (!f.a.constant) usesX = true;
        logs.push(mul(num(f.e), f.a.term.b, fn('ln', f.a.term.a)));
      } else return null;
    }
    parts.push(add(...logs));
  }
  if (!usesX) return null;
  // les deux membres sont des exponentielles positives : de signes opposés, aucune solution
  if (signs[0] !== signs[1]) return { roots: [], complete: true };
  let eq: RatFn;
  try {
    eq = toRatFn(add(parts[0]!, mul(num(-1), parts[1]!)));
  } catch (err) {
    if (err instanceof NormalFail) return null;
    throw err;
  }
  const sol = univariateSolutions(eq, name, depth + 1);
  if (!sol) return null;
  const kept = sol.roots.filter((r) => {
    try {
      return !rfIsZero(toRatFn(substitute(polyToTerm(e.d), { [name]: r })));
    } catch (err) {
      if (err instanceof NormalFail) return false;
      throw err;
    }
  });
  return { roots: kept, complete: sol.complete };
}

function positiveConstant(t: Term): boolean {
  try {
    const r = toRatFn(t);
    for (const p of [r.n, r.d])
      for (const { m } of p.t.values()) if (m.some((f) => !f.a.constant)) return false;
  } catch {
    return false;
  }
  const v = evalFloat(t, {});
  return Number.isFinite(v) && v > 1e-9;
}

/** E s'annule-t-il exactement en x = r ? (null : indécidable, ex. ln(2) dans le calcul) */
export function vanishesAt(e: Term, name: string, r: Term): boolean | null {
  try {
    const v = toRatFn(substitute(e, { [name]: r }));
    if (rfIsZero(v)) return true;
    // forme non nulle sur des racines et des rationnels seulement ⇒ valeur non nulle
    // (racines de premiers distincts linéairement indépendantes sur ℚ)
    for (const { m } of v.n.t.values()) if (m.some((f) => f.a.kind === 'opaque')) return null;
    return false;
  } catch (err) {
    if (err instanceof NormalFail) return null;
    throw err;
  }
}
